import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { num } from "../lib/format";
import { Icon } from "../lib/icons";
import { Button, Panel, Segmented, Skeleton, TIP, cn } from "./ui";

// Обзор → «Календарь активности»: one calendar year (1 Jan – 31 Dec, switchable) of daily movement
// as a heatmap (weeks × Mon–Sun).
// Written for people, not analysts: a one-line "how to read it", a legend with real unit ranges,
// days before the data starts shown as "нет данных" (not as quiet days), plain-sentence takeaways,
// and a click on any day shows what moved that day (GET /dashboard/day).

type Day = { date: string; supply: number; shipment: number; defect: number; shipped_products: number };
type CalendarData = { year: number; years: number[]; first: string | null; last: string | null; days: Day[] };
type Metric = "shipment" | "supply";
type DayDetail = {
  date: string;
  totals: { supply: number; shipment: number; defect: number; return_qty: number; shipped_products: number; supplied_products: number };
  top: Array<{ product_id: string; product_name: string; brand: string | null; category: string | null; shipment: number; supply: number }>;
};

const WEEKDAYS = ["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Вс"];
const WEEKDAYS_FULL = ["понедельник", "вторник", "среда", "четверг", "пятница", "суббота", "воскресенье"];
const MONTHS = ["Янв", "Фев", "Мар", "Апр", "Май", "Июн", "Июл", "Авг", "Сен", "Окт", "Ноя", "Дек"];
const COLOR: Record<Metric, string> = { shipment: "var(--accent-2)", supply: "var(--accent)" };
const LEVEL_ALPHA = [0, 0.22, 0.42, 0.68, 1];
const VERB: Record<Metric, string> = { shipment: "отгружено", supply: "поступило" };
const HATCH = "repeating-linear-gradient(45deg, rgb(var(--border)) 0 2px, transparent 2px 5px)";

const iso = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const parse = (s: string) => new Date(Number(s.slice(0, 4)), Number(s.slice(5, 7)) - 1, Number(s.slice(8, 10)));
const weekdayIdx = (d: Date) => (d.getDay() + 6) % 7; // Mon = 0
const dmy = (s: string) => parse(s).toLocaleDateString("ru-RU");
const longDate = (s: string) => parse(s).toLocaleDateString("ru-RU", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

// data: inside the data range · before: before the data starts · future: this year, not yet in
// the data · outside: padding days of the neighbouring year (hidden)
type Cell = { date: string; day: Day | null; state: "data" | "before" | "future" | "outside" };

export function ActivityCalendar() {
  const [metric, setMetric] = useState<Metric>("shipment");
  const [year, setYear] = useState<number | null>(null); // null = the latest year with data
  const [hover, setHover] = useState<{ cell: Cell; x: number; y: number } | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [box, setBox] = useState<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(0);
  const { data, isLoading, error } = useQuery({
    queryKey: ["calendar", year],
    queryFn: () => api.get<CalendarData>("/dashboard/calendar", { year: year ?? undefined }).then((r) => r.data),
    staleTime: 10 * 60 * 1000,
    placeholderData: (prev) => prev, // keep the old year on screen while the next one loads
  });

  useEffect(() => {
    if (!box) return;
    setWidth(box.clientWidth);
    const ro = new ResizeObserver((e) => setWidth(Math.round(e[0].contentRect.width)));
    ro.observe(box);
    return () => ro.disconnect();
  }, [box]);

  const model = useMemo(() => {
    if (!data?.first || !data.last) return null;
    const byDate = new Map(data.days.map((d) => [d.date, d]));
    const y = data.year;
    const jan1 = new Date(y, 0, 1);
    const dec31 = new Date(y, 11, 31);
    const start = new Date(jan1);
    start.setDate(start.getDate() - weekdayIdx(start)); // Monday on/before 1 Jan
    const end = new Date(dec31);
    end.setDate(end.getDate() + (6 - weekdayIdx(end))); // Sunday on/after 31 Dec
    const firstY = iso(jan1);
    const lastY = iso(dec31);
    const weeks: Cell[][] = [];
    const cur = new Date(start);
    while (cur <= end) {
      const col: Cell[] = [];
      for (let i = 0; i < 7; i++) {
        const k = iso(cur);
        const state: Cell["state"] =
          k < firstY || k > lastY ? "outside" : k < data.first ? "before" : k > data.last ? "future" : "data";
        col.push({ date: k, day: byDate.get(k) ?? null, state });
        cur.setDate(cur.getDate() + 1);
      }
      weeks.push(col);
    }
    const hasBefore = weeks.some((w) => w.some((c) => c.state === "before"));
    const hasFuture = weeks.some((w) => w.some((c) => c.state === "future"));
    return { weeks, firstData: data.first, lastData: data.last, year: y, years: data.years, hasBefore, hasFuture };
  }, [data]);

  const stats = useMemo(() => {
    if (!model) return null;
    const cells = model.weeks.flat().filter((c) => c.state === "data");
    const val = (c: Cell) => (c.day ? c.day[metric] : 0);
    const nonZero = cells.map(val).filter((v) => v > 0).sort((a, b) => a - b);
    const q = (p: number) => Math.round(nonZero[Math.min(nonZero.length - 1, Math.floor(p * nonZero.length))] ?? 0);
    const cuts = [q(0.25), q(0.5), q(0.75)];
    const level = (v: number) => (v <= 0 ? 0 : v <= cuts[0] ? 1 : v <= cuts[1] ? 2 : v <= cuts[2] ? 3 : 4);
    const total = cells.reduce((a, c) => a + val(c), 0);
    const best = cells.reduce<Cell | null>((b, c) => (!b || val(c) > val(b) ? c : b), null);
    const sum = Array(7).fill(0);
    const cnt = Array(7).fill(0);
    for (const c of cells) {
      const i = weekdayIdx(parse(c.date));
      sum[i] += val(c);
      cnt[i] += 1;
    }
    const avg = sum.map((s, i) => (cnt[i] ? s / cnt[i] : 0));
    const busiest = avg.indexOf(Math.max(...avg));
    const quietest = avg.indexOf(Math.min(...avg));
    const ranges = [
      "0",
      `1–${num(cuts[0])}`,
      `${num(cuts[0] + 1)}–${num(cuts[1])}`,
      `${num(cuts[1] + 1)}–${num(cuts[2])}`,
      `> ${num(cuts[2])}`,
    ];
    return { level, total, best, avg, busiest, quietest, ranges, activeDays: nonZero.length, days: cells.length, val };
  }, [model, metric]);

  const gap = 3;
  const labelW = 26;
  // every square carries its day-of-month, so it needs ≥16px; narrower screens scroll sideways
  const nWeeks = model?.weeks.length ?? 53;
  const cell = Math.max(16, Math.min(24, Math.floor((Math.max(0, width - labelW) + gap) / nWeeks) - gap));

  // when the year doesn't fit, open scrolled to the newest weeks (the ones people care about)
  useEffect(() => {
    if (!box || !model) return;
    // a past year opens at January; the current one at its newest weeks
    const lastIdx = model.weeks.findIndex((w) => w.some((c) => c.date === model.lastData));
    box.scrollLeft = lastIdx >= 0 ? Math.max(0, lastIdx * (cell + gap) - box.clientWidth + 80) : 0;
  }, [box, model, cell, width]);
  const color = COLOR[metric];

  return (
    <Panel
      title="Календарь активности"
      actions={
        <div className="flex flex-wrap items-center gap-2">
          {model && model.years.length > 1 && (
            <Segmented
              value={String(model.year)}
              onChange={(v) => {
                setSelected(null);
                setYear(Number(v));
              }}
              options={model.years.map((y) => ({ value: String(y), label: `${y}` }))}
            />
          )}
          <Segmented
            value={metric}
            onChange={setMetric}
            options={[
              { value: "shipment", label: "Отгрузка" },
              { value: "supply", label: "Поставка" },
            ]}
          />
        </div>
      }
    >
      {isLoading ? (
        <Skeleton className="h-[190px] rounded-lg" />
      ) : error || !model || !stats ? (
        <div className="py-8 text-center text-sm text-text-2">Нет данных для календаря</div>
      ) : (
        <div className="space-y-5">
          <div className="min-w-0">
            <p className="mb-4 text-sm text-text-2">
              <b className="text-text">{model.year} год</b>, с 1 января по 31 декабря. Каждый квадрат — один день, чем темнее
              цвет, тем больше товара {VERB[metric]} за день.{" "}
              <span className="text-text">Нажмите на день</span>, чтобы увидеть, что двигалось.
            </p>

            <div ref={setBox} className="scroll-thin overflow-x-auto pb-1" onScroll={() => setHover(null)}>
              {width > 0 && (
                <div className="inline-block">
                  {/* month labels */}
                  <div className="relative mb-1.5 h-4" style={{ marginLeft: labelW }}>
                    {model.weeks.map((w, i) => {
                      // label the column holding the 1st of a month (inside the shown year)
                      const firstOfMonth = w.find((c) => c.state !== "outside" && parse(c.date).getDate() === 1);
                      return firstOfMonth ? (
                        <span key={i} className="absolute text-xs font-medium text-text-2" style={{ left: i * (cell + gap) }}>
                          {MONTHS[parse(firstOfMonth.date).getMonth()]}
                        </span>
                      ) : null;
                    })}
                  </div>
                  <div className="flex">
                    {/* weekday labels stay put while the weeks scroll sideways (phones) */}
                    <div className="sticky left-0 z-[1] flex flex-col bg-surface" style={{ width: labelW, gap }}>
                      {WEEKDAYS.map((d) => (
                        <span key={d} className="text-2xs text-text-2" style={{ height: cell, lineHeight: `${cell}px` }}>
                          {d}
                        </span>
                      ))}
                    </div>
                    <div className="flex" style={{ gap }}>
                      {model.weeks.map((w, wi) => (
                        <div key={wi} className="flex flex-col" style={{ gap }}>
                          {w.map((c) => {
                            const lvl = c.state === "data" ? stats.level(stats.val(c)) : 0;
                            const isLast = c.date === model.lastData;
                            const isSel = c.date === selected;
                            return (
                              <button
                                key={c.date}
                                type="button"
                                disabled={c.state !== "data"}
                                tabIndex={c.state === "data" ? undefined : -1}
                                aria-label={c.state === "data" ? `${longDate(c.date)}: ${num(stats.val(c))} шт` : undefined}
                                onClick={() => setSelected(isSel ? null : c.date)}
                                onMouseEnter={(e) => {
                                  if (c.state === "outside") return;
                                  const r = e.currentTarget.getBoundingClientRect();
                                  setHover({ cell: c, x: r.left + r.width / 2, y: r.top });
                                }}
                                onMouseLeave={() => setHover(null)}
                                className={cn(
                                  "grid place-items-center rounded-[4px] transition-shadow disabled:cursor-default",
                                  c.state === "outside" && "invisible",
                                  c.state === "future" && "border border-dashed border-border",
                                  c.state === "data" && lvl === 0 && "bg-surface-2",
                                  c.state === "data" && "hover:ring-2 hover:ring-text/30",
                                  isLast && "ring-2 ring-text/60",
                                  isSel && "ring-2 ring-accent ring-offset-1 ring-offset-surface",
                                )}
                                style={{
                                  width: cell,
                                  height: cell,
                                  background:
                                    c.state === "before"
                                      ? HATCH
                                      : c.state === "data" && lvl > 0
                                        ? `rgb(${color} / ${LEVEL_ALPHA[lvl]})`
                                        : undefined,
                                }}
                              >
                                {c.state !== "outside" && (
                                  <span
                                    className={cn(
                                      "block text-center font-medium leading-none tnum",
                                      lvl >= 3 ? "text-white" : c.state === "data" ? "text-text-2" : "text-muted/70",
                                      parse(c.date).getDate() === 1 && "font-bold",
                                    )}
                                    style={{ fontSize: cell >= 21 ? 10 : cell >= 18 ? 9 : 8.5 }}
                                  >
                                    {parse(c.date).getDate()}
                                  </span>
                                )}
                              </button>
                            );
                          })}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* legend with real ranges */}
            <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-text-2">
              <span className="font-medium text-text">Шт. за день:</span>
              {stats.ranges.map((r, i) => (
                <span key={i} className="flex items-center gap-1.5">
                  <span
                    className={cn("h-3 w-3 rounded-[3px]", i === 0 && "bg-surface-2")}
                    style={i ? { background: `rgb(${color} / ${LEVEL_ALPHA[i]})` } : undefined}
                  />
                  <span className="tnum">{r}</span>
                </span>
              ))}
              {model.hasBefore && (
                <span className="flex items-center gap-1.5">
                  <span className="h-3 w-3 rounded-[3px]" style={{ background: HATCH }} />
                  нет данных (до {dmy(model.firstData)})
                </span>
              )}
              {model.hasFuture && (
                <span className="flex items-center gap-1.5">
                  <span className="h-3 w-3 rounded-[3px] border border-dashed border-border" />
                  ещё впереди
                </span>
              )}
              <span className="flex items-center gap-1.5">
                <span className="h-3 w-3 rounded-[3px] ring-2 ring-text/60" />
                последний день
              </span>
            </div>
          </div>

          {/* right: selected day, or plain-language takeaways */}
          <div className="border-t border-border pt-5">
            {selected ? (
              <DayPanel date={selected} metric={metric} onClose={() => setSelected(null)} />
            ) : (
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-4">
                <Takeaway icon="box" title={`Всего ${VERB[metric]} за ${model.year} год`}>
                  <b className="text-text tnum">{num(stats.total)} шт</b> за {num(stats.activeDays)} дней с движением (из {num(stats.days)})
                </Takeaway>
                <Takeaway icon="calendarDay" title="Самый загруженный день недели">
                  <b className="text-text">{WEEKDAYS_FULL[stats.busiest]}</b> — в среднем {num(stats.avg[stats.busiest])} шт; спокойнее всего —{" "}
                  {WEEKDAYS_FULL[stats.quietest]} ({num(stats.avg[stats.quietest])} шт)
                </Takeaway>
                {stats.best && stats.val(stats.best) > 0 && (
                  <Takeaway icon="star" title="Рекордный день">
                    <button type="button" className="font-semibold text-accent hover:underline" onClick={() => setSelected(stats.best!.date)}>
                      {dmy(stats.best.date)}
                    </button>{" "}
                    — {num(stats.val(stats.best))} шт
                  </Takeaway>
                )}
                <div>
                  <div className="mb-2 text-xs font-medium text-text-2">В среднем по дням недели, шт</div>
                  <WeekdayBars avg={stats.avg} color={color} top={stats.busiest} />
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {hover &&
        createPortal(
          <div
            className={cn("pointer-events-none fixed z-[75] -translate-x-1/2 -translate-y-full whitespace-nowrap", TIP)}
            style={{ left: hover.x, top: hover.y - 6 }}
          >
            <div className="mb-0.5 font-semibold first-letter:uppercase">{longDate(hover.cell.date)}</div>
            {hover.cell.state === "before" ? (
              <div className="text-bg/70">Данных за этот день нет</div>
            ) : hover.cell.state === "future" ? (
              <div className="text-bg/70">День ещё не наступил (или данных пока нет)</div>
            ) : hover.cell.day ? (
              <div className="space-y-0.5 text-bg/70 tnum">
                <div>
                  Отгружено: <b className="text-bg">{num(hover.cell.day.shipment)}</b> шт · {num(hover.cell.day.shipped_products)} тов.
                </div>
                <div>
                  Поступило: <b className="text-bg">{num(hover.cell.day.supply)}</b> шт
                </div>
                {hover.cell.day.defect > 0 && (
                  <div>
                    Брак: <b className="text-bg">{num(hover.cell.day.defect)}</b>
                  </div>
                )}
                <div className="pt-0.5 text-2xs text-bg/60">Нажмите — подробнее</div>
              </div>
            ) : (
              <div className="text-bg/70">Движения не было</div>
            )}
          </div>,
          document.body,
        )}
    </Panel>
  );
}

function Takeaway({ icon, title, children }: { icon: string; title: string; children: React.ReactNode }) {
  return (
    <div className="flex gap-3">
      <span className="grid h-8 w-8 flex-none place-items-center rounded-lg bg-accent-weak text-accent">
        <Icon name={icon} size={16} />
      </span>
      <div className="min-w-0 text-sm leading-snug text-text-2">
        <div className="text-xs font-medium text-text-2">{title}</div>
        <div className="mt-0.5">{children}</div>
      </div>
    </div>
  );
}

function WeekdayBars({ avg, color, top }: { avg: number[]; color: string; top: number }) {
  const max = Math.max(...avg, 1);
  return (
    <div className="flex h-[92px] items-end gap-1.5">
      {avg.map((v, i) => (
        <div key={i} className="flex flex-1 flex-col items-center gap-1" title={`${WEEKDAYS_FULL[i]}: в среднем ${num(v)} шт`}>
          <span className="text-2xs text-text-2 tnum">{num(v)}</span>
          <span
            className="w-full rounded-t-[3px]"
            style={{ height: Math.max(3, (v / max) * 52), background: `rgb(${color} / ${i === top ? 1 : 0.4})` }}
          />
          <span className={cn("text-2xs", i === top ? "font-semibold text-text" : "text-text-2")}>{WEEKDAYS[i]}</span>
        </div>
      ))}
    </div>
  );
}

/** What moved on one day — totals and the top products (by shipment, then supply). */
function DayPanel({ date, metric, onClose }: { date: string; metric: Metric; onClose: () => void }) {
  const navigate = useNavigate();
  const { data, isLoading, error } = useQuery({
    queryKey: ["calendar-day", date],
    queryFn: () => api.get<DayDetail>("/dashboard/day", { date }).then((r) => r.data),
  });
  return (
    <div>
      <div className="mb-3 flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <div className="text-xs font-medium text-text-2">Выбранный день</div>
          <div className="text-base font-semibold first-letter:uppercase">{longDate(date)}</div>
        </div>
        <Button size="sm" variant="ghost" onClick={onClose}>
          <Icon name="x" size={14} />
          Закрыть
        </Button>
      </div>
      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-9" />
          ))}
        </div>
      ) : error || !data ? (
        <div className="text-sm text-danger">Не удалось загрузить день</div>
      ) : (
        <>
          <div className="mb-3 grid max-w-xl grid-cols-3 gap-2">
            <DayStat label="Отгружено" value={num(data.totals.shipment)} sub={`${num(data.totals.shipped_products)} тов.`} strong={metric === "shipment"} />
            <DayStat label="Поступило" value={num(data.totals.supply)} sub={`${num(data.totals.supplied_products)} тов.`} strong={metric === "supply"} />
            <DayStat label="Брак" value={num(data.totals.defect)} danger={data.totals.defect > 0} />
          </div>
          {data.top.length === 0 ? (
            <div className="py-4 text-center text-sm text-text-2">Движения по товарам не было</div>
          ) : (
            <>
              <div className="mb-1 text-xs font-medium text-text-2">Больше всего двигалось</div>
              <div className="grid grid-cols-1 gap-x-6 divide-y divide-border md:grid-cols-2 md:divide-y-0">
                {data.top.map((p) => (
                  <button
                    key={p.product_id}
                    type="button"
                    onClick={() => navigate(`/product/${encodeURIComponent(p.product_id)}`)}
                    className="-mx-2 flex w-[calc(100%+1rem)] items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-surface-3"
                  >
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-medium">{p.product_name}</span>
                      <span className="block truncate text-xs text-text-2">{[p.brand, p.category].filter(Boolean).join(" · ")}</span>
                    </span>
                    <span className="flex-none text-right text-xs tnum">
                      {p.shipment > 0 && <span className="block font-semibold text-text">−{num(p.shipment)}</span>}
                      {p.supply > 0 && <span className="block text-ok">+{num(p.supply)}</span>}
                    </span>
                  </button>
                ))}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}

function DayStat({ label, value, sub, strong, danger }: { label: string; value: string; sub?: string; strong?: boolean; danger?: boolean }) {
  return (
    <div className={cn("min-w-0 rounded-lg border px-2.5 py-2", strong ? "border-accent/40 bg-accent-weak/50" : "border-border bg-surface-3")}>
      <div className="truncate text-xs text-text-2">{label}</div>
      <div className={cn("truncate text-md font-semibold tnum", danger && "text-danger")}>{value}</div>
      {sub && <div className="truncate text-2xs text-text-2">{sub}</div>}
    </div>
  );
}
